/**
 * Feature flags - the staged rollout rules.
 *
 * Everything beyond the core loop is BUILT for V1 and SWITCHED ON progressively,
 * so Mitali, Mary and Jayant get the gentlest possible switch from the old system
 * rather than all of it at once (v1-scope-and-design-brief section 41).
 *
 * These are pure decisions: a flag record plus a role in, a boolean out. The HTTP
 * gate and the admin screen both call this same function, which is the whole point -
 * one rule, not a server rule and a menu rule that drift apart.
 */

/** app_user.role, matching the CHECK constraint in migration 0001. */
export const ROLES = ['owner', 'admin', 'cataloguer', 'fulfilment', 'system'] as const
export type Role = (typeof ROLES)[number]

/**
 * off          - nobody sees it, including the owner. A dark feature is dark.
 * on_for_role  - only the roles named in target_roles see it. The rollout dial.
 * on           - everybody sees it. The end state of a successful rollout.
 */
export const FLAG_STATES = ['off', 'on_for_role', 'on'] as const
export type FlagState = (typeof FLAG_STATES)[number]

/** The extras that get switched on progressively. Core-loop screens are never flagged. */
export const FLAG_KEYS = [
  'storefront',
  'crm',
  'group_management',
  'metrics',
  'task_batching',
  'multi_carrier_shipping',
] as const
export type FlagKey = (typeof FLAG_KEYS)[number]

export interface FlagRecord {
  key: string
  description: string
  state: FlagState
  /** Only meaningful when state is on_for_role. Stored as feature_flag_audience rows. */
  targetRoles: Role[]
  notes: string | null
}

export function isFlagState(value: unknown): value is FlagState {
  return typeof value === 'string' && (FLAG_STATES as readonly string[]).includes(value)
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/**
 * The one decision. Called by the API gate before a handler runs and by the PWA
 * before a tile is drawn.
 *
 * Fails closed: an unknown flag key, or a flag missing from the store entirely,
 * is off. A typo in a gate therefore hides a feature - the safe direction - rather
 * than exposing an unfinished one.
 */
export function canSee(flag: FlagRecord | undefined, role: Role): boolean {
  if (!flag) return false
  switch (flag.state) {
    case 'on':
      return true
    case 'on_for_role':
      return flag.targetRoles.includes(role)
    case 'off':
      return false
    default:
      return false
  }
}

/** Convenience over a whole store, for the /me/features response. */
export function visibleFlagKeys(flags: readonly FlagRecord[], role: Role): string[] {
  return flags
    .filter((f) => canSee(f, role))
    .map((f) => f.key)
    .sort()
}

/**
 * Which roles can currently see this flag - what the admin screen prints under
 * each toggle so Maulik can read the blast radius without doing the logic himself.
 */
export function audienceOf(flag: FlagRecord): Role[] {
  if (flag.state === 'on') return [...ROLES]
  if (flag.state === 'off') return []
  return ROLES.filter((r) => flag.targetRoles.includes(r))
}

/**
 * Normalises an admin toggle before it is written.
 *
 * on_for_role with no roles named is a foot-gun: it looks enabled in the list and
 * shows nobody anything. It collapses to off, honestly, rather than being stored
 * as a lie. Roles are only kept for on_for_role, so flipping to on and back to
 * on_for_role cannot silently resurrect a stale audience.
 */
export function normaliseToggle(state: FlagState, targetRoles: readonly Role[]): {
  state: FlagState
  targetRoles: Role[]
} {
  if (state !== 'on_for_role') return { state, targetRoles: [] }
  const roles = ROLES.filter((r) => targetRoles.includes(r))
  if (roles.length === 0) return { state: 'off', targetRoles: [] }
  return { state: 'on_for_role', targetRoles: roles }
}
